import { Link } from 'react-router-dom';
import { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Box, H, Text, Flex, DropdownOptions, TooltipPopup } from 'dash-ui';
import Header from 'flow-components/Header/Header';
import Footer from 'flow-components/Footer/Footer';
import { RoutesPath } from 'flow-components/routes/routes';
import Container from '../Container/Container';
import { useBrowserTabTitle } from 'common/hooks/use-browser-tab-title/use-browser-tab-title';

function PrivacyPolicy() {
  const { t } = useTranslation(['pages']);
  const theme = useTheme();
  useBrowserTabTitle(
    `${t('common:labels.browserTabTitles.privacyPolicy')} ${t(
      'common:labels.dashSuffix'
    )}`
  );

  return (
    <Box h="100%">
      <DropdownOptions />
      <TooltipPopup />
      <Header withProfileDropdown={false} />
      <Container>
        <Flex flexDirection="column">
          <H lg> {t('pages:footer:policyLabel')}</H>
          <H sm>Last modified: June 21, 2023</H>
          <Flex mt={2} w="100%" flexDirection="column">
            <Text color={theme.colors.n80}>
              DASH (“
              <Text as="span" semibold color={theme.colors.n80}>
                Company
              </Text>
              ” or “
              <Text as="span" semibold color={theme.colors.n80}>
                We
              </Text>
              ”) respects your privacy and is committed to protecting it through
              our compliance with this policy.
              <br />
              This policy describes the types of information we may collect from
              you or that you may provide when you visit the website
              www.dashtodash.com (our “
              <Text as="span" semibold color={theme.colors.n80}>
                Website
              </Text>
              “), including any content, functionality, and services offered on
              or through the Company’s platform (the "
              <Text as="span" semibold color={theme.colors.n80}>
                Platform
              </Text>
              ") at the same web address, whether as a guest or a registered
              user, and our practices for collecting, using, maintaining,
              protecting, and disclosing that information.
              <br />
              This policy applies to information we collect:
              <br />
              <ul>
                <li>On this Website or through the Platform.</li>
                <li>
                  In email, text, and other electronic messages between you and
                  this Website.
                </li>
                <li>
                  Through mobile and desktop applications you download from this
                  Website, which provide dedicated non-browser-based interaction
                  between you and this Website.
                </li>
                <li>
                  When you interact with our advertising and applications on
                  third-party websites and services, if those applications or
                  advertising include links to this policy.
                </li>
              </ul>
              It does not apply to information collected by:
              <br />
              <ul>
                <li>
                  Us offline or through any other means, including on any other
                  website operated by Company or any third party (including our
                  affiliates and subsidiaries); or
                </li>
                <li>
                  Any third party (including our affiliates and subsidiaries),
                  including through any application or content (including
                  advertising) that may link to or be accessible from or on the
                  Website
                </li>
              </ul>
              Please read this policy carefully to understand our policies and
              practices regarding your information and how we will treat it. If
              you do not agree with our policies and practices, your choice is
              not to use our Website or the Platform. By accessing or using this
              Website or the Platform, you agree to this Privacy Policy. This
              Privacy Policy may change from time to time (see Changes to Our
              Privacy Policy). Your continued use of this Website and the
              Platform after we make changes is deemed to be acceptance of those
              changes, so please check this Privacy Policy periodically for
              updates.
            </Text>
          </Flex>
          <Flex mt={2} w="100%" flexDirection="column">
            <Text semibold lg>
              Children Under the Age of 18
            </Text>
            <Text color={theme.colors.n80}>
              No one under age 18 may provide any personal information to or on
              the Website or the Platform without parental or guardian consent.
              If you are under 18, do not use or provide any information on this
              Website or the Platform, or through any of their respective
              features, register on the Website or the Platform, make any
              purchases, use any of the interactive or public comment features
              of this Website, or provide any information about yourself to us,
              including your name, address, telephone number, email address, or
              any screen name or user name you may use, in each case without
              parental or guardian consent. If we learn we have collected or
              received personal information from a child under 18 without
              verification of parental or guardian consent, we will delete that
              information. If you believe we might have any information from or
              about a child under 18 without parental or guardian consent,
              please contact us at support@dashtodash.com
            </Text>
          </Flex>
          <Flex mt={2} w="100%" flexDirection="column">
            <Text semibold lg>
              Information We Collect About You and How We Collect It
            </Text>
            <Text color={theme.colors.n80}>
              We collect several types of information from and about users of
              our Website and the Platform, including information:
              <br />
              <ul>
                <li>
                  By which you may be personally identified, such as [name,
                  postal address, e-mail address, telephone number, social
                  security number] or any other identifier by which you may be
                  contacted online or offline] (“personal information“);
                </li>
                <li>
                  That is about you but individually does not identify you;
                  and/or
                </li>
                <li>
                  About your internet connection, the equipment you use to
                  access our Website and the Platform, and usage details.
                </li>
              </ul>
              We collect this information:
              <br />
              <ul>
                <li>Directly from you when you provide it to us.</li>
                <li>
                  Automatically as you navigate through the site. Information
                  collected automatically may include usage details, IP
                  addresses, and information collected through cookies, web
                  beacons, and other tracking technologies.
                </li>
                <li>From third parties, for example, our business partners.</li>
              </ul>
              <i>Information You Provide to Us</i>
              <br />
              The information we collect on or through our Website or the
              Platform may include:
              <br />
              <ul>
                <li>
                  Information that you provide by filling in forms on our
                  Website or the Platform. This includes information provided at
                  the time of registering to use our Website or the Platform, or
                  posting material on the Website. We may also ask you for
                  information when you report a problem with our Website or the
                  Platform.
                </li>
                <li>
                  Records and copies of your correspondence (including email
                  addresses), if you contact us.
                </li>
                <li>
                  Your responses to surveys that we might ask you to complete
                  for research purposes.
                </li>
                <li>
                  Details of transactions you carry out through our Website and
                  of the fulfillment of your orders. You may be required to
                  provide financial information before placing an order through
                  our Website.
                </li>
                <li>Your search queries on the Website or the Platform.</li>
              </ul>
              You also may provide information to be published or displayed
              (hereinafter, “posted“) on public areas of the Website, or
              transmitted to other users of the Website or third parties
              (collectively, “User Contributions“). Your User Contributions are
              posted on and transmitted to others at your own risk. Please be
              aware that no security measures are perfect or impenetrable.
              Additionally, we cannot control the actions of other users of the
              Website with whom you may choose to share your User Contributions.
              Therefore, we cannot and do not guarantee that your User
              Contributions will not be viewed by unauthorized persons.
              <br />
              <i>
                Information We Collect Through Automatic Data Collection
                Technologies
              </i>
              <br />
              As you navigate through and interact with our Website or the
              Platform, we may use automatic data collection technologies to
              collect certain information about your equipment, browsing
              actions, and patterns, including:
              <br />
              <ul>
                <li>
                  Details of your visits to our Website or the Platform,
                  including traffic data, location data, logs, and other
                  communication data and the resources that you access and use
                  on the Website or the Platform.
                </li>
                <li>
                  Information about your computer and internet connection,
                  including your IP address, operating system, and browser
                  type..
                </li>
              </ul>
              We also may use these technologies to collect information about
              your online activities over time and across third-party websites
              or other online services (behavioral tracking).
              <br />
              The information we collect automatically is only statistical data
              and does not include personal information, but we may maintain it
              or associate it with personal information we collect in other ways
              or receive from third parties. It helps us to improve our Website
              and the Platform, and to deliver a better and more personalized
              service, including by enabling us to:
              <br />
              <ul>
                <li>Estimate our audience size and usage patterns.</li>
                <li>
                  Store information about your preferences, allowing us to
                  customize our Website and the Platform according to your
                  individual interests.
                </li>
                <li>Speed up your searches.</li>
                <li>
                  Recognize you when you return to our Website and the Platform.
                </li>
              </ul>
              The technologies we use for this automatic data collection may
              include:
              <br />
              <ul>
                <li>
                  <Text as="span" semibold color={theme.colors.n80}>
                    Cookies (or browser cookies).
                  </Text>
                  &nbsp; A cookie is a small file placed on the hard drive of
                  your computer. You may refuse to accept browser cookies by
                  activating the appropriate setting on your browser. However,
                  if you select this setting you may be unable to access certain
                  parts of our Website or the Platform. Unless you have adjusted
                  your browser setting so that it will refuse cookies, our
                  system will issue cookies when you direct your browser to our
                  Website or the Platform.
                </li>
                <li>
                  <Text as="span" semibold color={theme.colors.n80}>
                    Flash Cookies.
                  </Text>
                  &nbsp; Certain features of our Website and the Platform may
                  use local stored objects (or Flash cookies) to collect and
                  store information about your preferences and navigation to,
                  from, and on our Website and the Platform. Flash cookies are
                  not managed by the same browser settings as are used for
                  browser cookies. For information about managing your privacy
                  and security settings for Flash cookies, see Choices About How
                  We Use and Disclose Your Information.
                </li>
                <li>
                  <Text as="span" semibold color={theme.colors.n80}>
                    Web Beacons.
                  </Text>
                  &nbsp; Pages of the Website, the Platform and our emails may
                  contain small electronic files known as web beacons (also
                  referred to as clear gifs, pixel tags, and single-pixel gifs)
                  that permit the Company, for example, to count users who have
                  visited those pages or opened an email and for other related
                  website statistics (for example, recording the popularity of
                  certain website content and verifying system and server
                  integrity).
                </li>
              </ul>
              We do not collect personal information automatically, but we may
              tie this information to personal information about you that we
              collect from other sources or you provide to us.
            </Text>
          </Flex>
          <Flex mt={2} w="100%" flexDirection="column">
            <Text semibold lg>
              Third-Party Use of Cookies and Other Tracking Technologies
            </Text>
            <Text color={theme.colors.n80}>
              Some content or applications, including advertisements, on the
              Website and the Platform are served by third-parties, including
              advertisers, ad networks and servers, content providers, and
              application providers. These third parties may use cookies alone
              or in conjunction with web beacons or other tracking technologies
              to collect information about you when you use our website. The
              information they collect may be associated with your personal
              information or they may collect information, including personal
              information, about your online activities over time and across
              different websites and other online services. They may use this
              information to provide you with interest-based (behavioral)
              advertising or other targeted content.
              <br />
              We do not control these third parties’ tracking technologies or
              how they may be used. If you have any questions about an
              advertisement or other targeted content, you should contact the
              responsible provider directly. For information about how you can
              opt out of receiving targeted advertising from many providers, see
              Choices About How We Use and Disclose Your Information.
            </Text>
          </Flex>
          <Flex mt={2} w="100%" flexDirection="column">
            <Text semibold lg>
              How We Use Your Information
            </Text>
            <Text color={theme.colors.n80}>
              We use information that we collect about you or that you provide
              to us, including any personal information:
              <br />
              <ul>
                <li>
                  To present our Website, the Platform and its contents to you.
                </li>
                <li>
                  To provide you with information, products, or services that
                  you request from us.
                </li>
                <li>To fulfill any other purpose for which you provide it.</li>
                <li>
                  To carry out our obligations and enforce our rights arising
                  from any contracts entered into between you and us, including
                  for billing and collection.
                </li>
                <li>
                  To notify you about changes to our Website, the Platform or
                  any products or services we offer or provide through it.
                </li>
                <li>
                  To allow you to participate in interactive features on our
                  Website and the Platform.
                </li>
                <li>
                  In any other way we may describe when you provide the
                  information.
                </li>
                <li>For any other purpose with your consent.</li>
              </ul>
              We may also use your information to contact you about our own and
              third-parties’ goods and services that may be of interest to you.
              For more information, see Choices About How We Use and Disclose
              Your Information.
              <br />
              We may use the information we have collected from you to enable us
              to display advertisements to our advertisers’ target audiences.
              Even though we do not disclose your personal information for these
              purposes without your consent, if you click on or otherwise
              interact with an advertisement, the advertiser may assume that you
              meet its target criteria.
            </Text>
          </Flex>
          <Flex mt={2} w="100%" flexDirection="column">
            <Text semibold lg>
              Disclosure of Your Information
            </Text>
            <Text color={theme.colors.n80}>
              We may disclose aggregated information about our users, and
              information that does not identify any individual, without
              restriction.
              <br />
              We may disclose personal information that we collect or you
              provide as described in this privacy policy:
              <br />
              <ul>
                <li>To our subsidiaries and affiliates.</li>
                <li>
                  To contractors, service providers, and other third parties we
                  use to support our business and who are bound by contractual
                  obligations to keep personal information confidential and use
                  it only for the purposes for which we disclose it to them.
                </li>
                <li>
                  To a buyer or other successor in the event of a merger,
                  divestiture, restructuring, reorganization, dissolution, or
                  other sale or transfer of some or all of the Company’s assets,
                  whether as a going concern or as part of bankruptcy,
                  liquidation, or similar proceeding, in which personal
                  information held by the Company about our Website users is
                  among the assets transferred.
                </li>
                <li>
                  To third parties to market their products or services to you
                  if you have not opted out of these disclosures. For more
                  information, see Choices About How We Use and Disclose Your
                  Information.
                </li>
                <li>To fulfill the purpose for which you provide it.</li>
                <li>
                  For any other purpose disclosed by us when you provide the
                  information.
                </li>
                <li>With your consent.</li>
              </ul>
              We may also disclose your personal information:
              <br />
              <ul>
                <li>
                  To comply with any court order, law, or legal process,
                  including to respond to any government or regulatory request.
                </li>
                <li>
                  To enforce or apply our&nbsp;
                  <Link target="_blank" to={RoutesPath.TermConditions}>
                    Terms of Use
                  </Link>{' '}
                  and other agreements, including for billing and collection
                  purposes.
                </li>
                <li>
                  If we believe disclosure is necessary or appropriate to
                  protect the rights, property, or safety of the Company, our
                  customers, or others. This includes exchanging information
                  with other companies and organizations for the purposes of
                  fraud protection and credit risk reduction.
                </li>
              </ul>
            </Text>
          </Flex>
          <Flex mt={2} w="100%" flexDirection="column">
            <Text semibold lg>
              Choices About How We Use and Disclose Your Information
            </Text>
            <Text color={theme.colors.n80}>
              We strive to provide you with choices regarding the personal
              information you provide to us. We have created mechanisms to
              provide you with the following control over your information:
              <br />
              <ul>
                <li>
                  <Text as="span" semibold color={theme.colors.n80}>
                    Tracking Technologies and Advertising.
                  </Text>
                  &nbsp; You can set your browser to refuse all or some browser
                  cookies, or to alert you when cookies are being sent. To learn
                  how you can manage your Flash cookie settings, visit the Flash
                  player settings page on Adobe’s website. If you disable or
                  refuse cookies, please note that some parts of this site may
                  then be inaccessible or not function properly.
                </li>
                <li>
                  <Text as="span" semibold color={theme.colors.n80}>
                    Disclosure of Your Information for Third-Party Advertising.
                  </Text>
                  &nbsp; If you do not want us to share your personal
                  information with unaffiliated or non-agent third parties for
                  promotional purposes, you can opt-out by sending us an email
                  with your request to support@dashtodash.com.
                </li>
                <li>
                  <Text as="span" semibold color={theme.colors.n80}>
                    Promotional Offers from the Company.
                  </Text>
                  &nbsp; If you do not wish to have your contact information
                  used by the Company to promote our own or third parties’
                  products or services, you can opt-out by sending us an email
                  stating your request to support@dashtodash.com. If we have
                  sent you a promotional email, you may send us a return email
                  asking to be omitted from future email distributions. This opt
                  out does not apply to information provided to the Company as a
                  result of a product purchase, warranty registration, product
                  service experience or other transactions.
                </li>
                <li>
                  <Text as="span" semibold color={theme.colors.n80}>
                    Targeted Advertising.
                  </Text>
                  &nbsp; If you do not want us to use information that we
                  collect or that you provide to us to deliver advertisements
                  according to our advertisers’ target-audience preferences, you
                  can opt-out by emailing your request to
                  support@dashtodash.com.
                </li>
              </ul>
              We do not control third parties’ collection or use of your
              information to serve interest-based advertising. However these
              third parties may provide you with ways to choose not to have your
              information collected or used in this way. You can opt out of
              receiving targeted ads from members of the Network Advertising
              Initiative
              <Text as="span" semibold color={theme.colors.n80}>
                (“NAI“)
              </Text>
              on the NAI’s website.
              <br />
              Nevada residents who wish to exercise their sale opt-out rights
              under Nevada Revised Statutes Chapter 603A may submit a request to
              this designated address: support@dashtodash.com. However, please
              know we do not currently sell data triggering that statute’s
              opt-out requirements.
            </Text>
          </Flex>
          <Flex mt={2} w="100%" flexDirection="column">
            <Text semibold lg>
              Accessing and Correcting Your Information
            </Text>
            <Text color={theme.colors.n80}>
              You can review and change your personal information by logging
              into the Website and the Platform and visiting your account
              profile page.
              <br />
              You may also send us an email at support@dashtodash.com to request
              access to, correct or delete any personal information that you
              have provided to us. We cannot delete your personal information
              except by also deleting your user account. We may not accommodate
              a request to change information if we believe the change would
              violate any law or legal requirement or cause the information to
              be incorrect.
              <br />
              If you delete your User Contributions from the Website, copies of
              your User Contributions may remain viewable in cached and archived
              pages, or might have been copied or stored by other Website users.
              Proper access and use of information provided on the Website,
              including User Contributions, is governed by our&nbsp;
              <Link target="_blank" to={RoutesPath.TermConditions}>
                Terms of Use
              </Link>
              .
            </Text>
          </Flex>
          <Flex mt={2} w="100%" flexDirection="column">
            <Text semibold lg>
              Your California Privacy Rights
            </Text>
            <Text color={theme.colors.n80}>
              California’s “Shine the Light” law (Civil Code Section § 1798.83)
              permits users of our Website and Platform that are California
              residents to request certain information regarding our disclosure
              of personal information to third parties for their direct
              marketing purposes. To make such a request, please send an email
              to support@dashtodash.com.
            </Text>
          </Flex>
          <Flex mt={2} w="100%" flexDirection="column">
            <Text semibold lg>
              Data Security
            </Text>
            <Text color={theme.colors.n80}>
              We have implemented measures designed to secure your personal
              information from accidental loss and from unauthorized access,
              use, alteration, and disclosure. All information you provide to us
              is stored on our secure servers behind firewalls. Any payment
              transactions will be encrypted using SSL technology.
              <br />
              The safety and security of your information also depends on you.
              Where we have given you (or where you have chosen) a password for
              access to certain parts of our Website or Platform, you are
              responsible for keeping this password confidential. We ask you not
              to share your password with anyone. We urge you to be careful
              about giving out information in public areas of the Website like
              message boards. The information you share in public areas may be
              viewed by any user of the Website.
              <br />
              Unfortunately, the transmission of information via the internet is
              not completely secure. Although we do our best to protect your
              personal information, we cannot guarantee the security of your
              personal information transmitted to our Website or the Platform.
              Any transmission of personal information is at your own risk. We
              are not responsible for circumvention of any privacy settings or
              security measures contained on the Website or the Platform.
            </Text>
          </Flex>
          <Flex mt={2} w="100%" flexDirection="column">
            <Text semibold lg>
              Changes to Our Privacy Policy
            </Text>
            <Text color={theme.colors.n80}>
              It is our policy to post any changes we make to our privacy policy
              on this page with a notice that the privacy policy has been
              updated on the Website home page. If we make material changes to
              how we treat our users’ personal information, we will notify you
              through a notice on the Website home page. The date the privacy
              policy was last revised is identified at the top of the page. You
              are responsible for ensuring we have an up-to-date active and
              deliverable email address for you, and for periodically visiting
              our Website and this privacy policy to check for any changes.
            </Text>
          </Flex>
          <Flex mt={2} w="100%" flexDirection="column">
            <Text semibold lg>
              Contact Information
            </Text>
            <Text color={theme.colors.n80}>
              To ask questions or comment about this privacy policy and our
              privacy practices, contact us at:
              <br />
              1000 Venetian Way
              <br />
              Suite 2202
              <br />
              Miami Beach, FL 33139
              <br />
            </Text>
          </Flex>
        </Flex>
      </Container>
      <Footer variation="dark" />
    </Box>
  );
}

export default PrivacyPolicy;
